import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const ResetPassword = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const isNative = Capacitor.isNativePlatform();
  const isIOS = Capacitor.getPlatform() === "ios";

  const handleInputFocus = useCallback(() => {
    setTimeout(() => {
      const el = document.activeElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 450);
  }, []);

  useEffect(() => {
    if (!isNative) return;
    const listeners: { remove: () => Promise<void> }[] = [];
    let cancelled = false;
    const showEvent = isIOS ? "keyboardWillShow" : "keyboardDidShow";
    const hideEvent = isIOS ? "keyboardWillHide" : "keyboardDidHide";
    void (async () => {
      const [showL, hideL] = await Promise.all([
        Keyboard.addListener(showEvent as "keyboardWillShow", (info) => {
          if (!cancelled) setKeyboardHeight(info.keyboardHeight);
        }),
        Keyboard.addListener(hideEvent as "keyboardWillHide", () => {
          if (!cancelled) setKeyboardHeight(0);
        }),
      ]);
      if (!cancelled) listeners.push(showL, hideL);
      else {
        showL.remove();
        hideL.remove();
      }
    })();
    return () => {
      cancelled = true;
      listeners.forEach((l) => l.remove());
    };
  }, [isNative, isIOS]);

  useEffect(() => {
    document.title = "Reset Password | Straiv";
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 8) {
      toast({ title: t('auth.resetErrors.tooShort'), description: t('auth.resetErrors.tooShortDescription'), variant: "destructive" });
      return;
    }
    if (password !== confirm) {
      toast({ title: t('auth.resetErrors.noMatch'), description: t('auth.resetErrors.noMatchDescription'), variant: "destructive" });
      return;
    }

    try {
      setLoading(true);
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast({ title: t('auth.resetErrors.resetFailed'), description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: t('auth.resetErrors.passwordUpdated'), description: t('auth.resetErrors.passwordUpdatedDescription') });
      navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const notAuthenticated = !user;

  return (
    <div 
      className="min-h-screen bg-gradient-bg flex items-center justify-center p-4 overflow-y-auto"
      style={{
        paddingBottom: keyboardHeight > 0 ? `calc(20px + ${keyboardHeight}px)` : undefined,
        WebkitOverflowScrolling: "touch",
      }}
    >
      <Card className="w-full max-w-md bg-gradient-card border-0">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold bg-gradient-primary bg-clip-text text-transparent">
            {t('auth.resetTitle')}
          </CardTitle>
          <CardDescription>
            {notAuthenticated
              ? t('auth.resetDescriptionUnauthenticated')
              : t('auth.resetDescriptionAuthenticated')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {notAuthenticated ? (
            <div className="text-center">
              <Button onClick={() => navigate("/auth")}>{t('auth.goToSignIn')}</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">{t('auth.newPassword')}</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={show ? "text" : "password"}
                    placeholder={t('auth.newPasswordPlaceholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={handleInputFocus}
                    className="pr-10"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShow((s) => !s)}
                    aria-label={show ? t('auth.hidePassword') : t('auth.showPassword')}
                  >
                    {show ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">{t('auth.confirmNewPassword')}</Label>
                <Input
                  id="confirm"
                  type={show ? "text" : "password"}
                  placeholder={t('auth.confirmNewPasswordPlaceholder')}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onFocus={handleInputFocus}
                  required
                />
              </div>

              <Button type="submit" className="w-full bg-gradient-primary text-black shadow-glow" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t('auth.updatePassword')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
